import { useState, useEffect, useRef } from 'react'
import { supabase } from '../supabaseClient'
import { invokeFunction } from '../supabaseFetch'
import { initAnalytics, identifyUser, resetAnalytics } from '../analytics'
import { setSentryUser } from '../sentry'

export function useAuth() {
  const [session, setSession] = useState(null)
  const [authLoading, setAuthLoading] = useState(true)
  const [passwordRecovery, setPasswordRecovery] = useState(false)
  const [activatingInvite, setActivatingInvite] = useState(false)
  const [reloadFlag, setReloadFlag] = useState(0)
  // Track whether we've already processed SIGNED_IN for the current user.
  // Supabase re-fires SIGNED_IN on every tab return (not just actual logins),
  // so we use this to skip redundant invite activation + loading gates.
  // useRef (not useState) — no re-render needed, and avoids hook count changes.
  const knownUserIdRef = useRef(null)

  async function activatePendingInvites() {
    const { data, error } = await supabase.rpc('activate_pending_invites')
    if (error) {
      console.error('Failed to activate pending invites:', error)
      setActivatingInvite(false)
      return
    }
    setActivatingInvite(false)
    if (data?.activated_count > 0) {
      setReloadFlag(f => f + 1)
    }
  }

  useEffect(() => {
    initAnalytics()
    // Use getUser() to validate JWT server-side (not just read from localStorage)
    // then getSession() for the access token only if user is valid
    supabase.auth.getUser().then(({ data: { user }, error }) => {
      if (error || !user) {
        setAuthLoading(false)
        return
      }
      return supabase.auth.getSession().then(({ data: { session } }) => {
        if (session) {
          knownUserIdRef.current = session.user.id
          setActivatingInvite(true)
          identifyUser(session.user.id, session.user.email)
          setSentryUser(session.user.id, session.user.email)
          activatePendingInvites()
        }
        setSession(session)
        setAuthLoading(false)
      })
    }).catch((err) => {
      console.warn('Baulit: Auth session recovery failed:', err?.message)
      setAuthLoading(false)
    })
    setTimeout(() => setAuthLoading(false), 5000)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      // Supabase re-fires SIGNED_IN on every tab visibility change (not just
      // actual logins). We must distinguish first-time sign-in from tab-return
      // re-emits to avoid showing the loading skeleton on every tab switch.
      if (_event === "PASSWORD_RECOVERY") {
        setSession(session)
        setPasswordRecovery(true)
        return
      }
      if (_event === "SIGNED_IN" && session) {
        // Check if this is a genuine new sign-in or a tab-return re-emit
        const isNewSignIn = knownUserIdRef.current !== session.user.id
        knownUserIdRef.current = session.user.id
        if (isNewSignIn) {
          // Genuine sign-in: update session, activate invites, show loading gate
          setSession(session)
          setActivatingInvite(true)
          identifyUser(session.user.id, session.user.email)
          setSentryUser(session.user.id, session.user.email)
          // Notify admin of new signups (fire-and-forget)
          const createdAt = session.user.created_at ? new Date(session.user.created_at) : null
          const isNewUser = createdAt && (Date.now() - createdAt.getTime() < 5 * 60 * 1000)
          if (isNewUser) {
            invokeFunction('notify-signup', session.access_token, {
              user_email: session.user.email,
              signup_at: session.user.created_at,
            }).catch(err => console.error('Signup notification error:', err))
          }
          await activatePendingInvites()
        }
        // Tab-return re-emit: silently ignore, keep existing session stable
        return
      }
      if (_event === "TOKEN_REFRESHED" && session) {
        // Token refreshed — don't update session reference (avoids re-renders)
        return
      }
      if (!session) {
        // Signed out
        setSession(null)
        knownUserIdRef.current = null
      }
    })
    return () => subscription.unsubscribe()
  }, [])

  function handleSignOut() {
    supabase.removeAllChannels()
    resetAnalytics()
    setSentryUser(null)
    // baulit_view and baulit_activeId removed — URL is now the source of truth
    supabase.auth.signOut();
    setSession(null);
    knownUserIdRef.current = null;
    setActivatingInvite(false);
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith("sb-")) localStorage.removeItem(key);
    });
  }

  return {
    session,
    authLoading,
    passwordRecovery, setPasswordRecovery,
    activatingInvite, setActivatingInvite,
    reloadFlag,
    handleSignOut,
  }
}
